import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

/* ── Stats strip ── */

const StatsSection = styled.section`
  background: var(--white);
  padding: 5rem 2.5rem;
  border-bottom: 1px solid var(--gray-5);
`;

const StatsInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const Stat = styled(motion.div)`
  text-align: center;
`;

const StatNumber = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--dark-2);
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.p`
  font-size: 0.78rem;
  color: var(--gray-3);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 400;
`;

/* ── About content ── */

const Section = styled.section`
  padding: 6rem 2.5rem;
  background: var(--off-white);
`;

const Inner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const Eyebrow = styled(motion.p)`
  font-size: 0.68rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--gray-3);
  font-weight: 500;
  margin-bottom: 1rem;
`;

const Heading = styled(motion.h2)`
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 700;
  color: var(--dark-2);
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin-bottom: 1.25rem;
`;

const Text = styled.p`
  font-size: 0.92rem;
  color: var(--gray-2);
  line-height: 1.8;
  font-weight: 300;

  & + & { margin-top: 0.75rem; }
`;

const FactList = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const Fact = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 1.1rem 0;
  border-bottom: 1px solid var(--gray-5);

  &:first-child { border-top: 1px solid var(--gray-5); }
`;

const FactLabel = styled.span`
  font-size: 0.78rem;
  color: var(--gray-3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const FactValue = styled.span`
  font-size: 0.9rem;
  color: var(--dark-2);
  font-weight: 500;
`;


/* ── Data ── */

const stats = [
  { number: '2025', label: 'Etablert' },
  { number: '100%', label: 'Privateid' },
  { number: 'Tech', label: 'Fokus' },
  { number: '10+', label: '\u00c5r horisont' },
];

const facts = [
  ['Selskapsform', 'Aksjeselskap (AS)'],
  ['Lokasjon', 'Bergen, Norge'],
  ['Eier', 'Martin Andreassen'],
  ['Fokus', 'Teknologi & Vekst'],
];


/* ── Component ── */

const About = () => (
  <>
    <StatsSection id="om">
      <StatsInner>
        {stats.map((s, i) => (
          <Stat
            key={s.label}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            <StatNumber>{s.number}</StatNumber>
            <StatLabel>{s.label}</StatLabel>
          </Stat>
        ))}
      </StatsInner>
    </StatsSection>

    <Section>
      <Inner>
        <TwoCol>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Eyebrow>Om selskapet</Eyebrow>
            <Heading>Bygget for langsiktig verdiskaping</Heading>
            <Text>
              Andreassen Kapital er et privat holdingselskap med en
              portefølje av selskaper innen teknologi og vekst.
              Vi fokuserer p&aring; langsiktig verdiskaping.
            </Text>
            <Text>
              Selskapet er 100&nbsp;% privateid og drives med en langsiktig
              horisont der t&aring;lmodighet og grundighet er sentrale verdier.
            </Text>
          </motion.div>
          <FactList
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            {facts.map(([l, v]) => (
              <Fact key={l}>
                <FactLabel>{l}</FactLabel>
                <FactValue>{v}</FactValue>
              </Fact>
            ))}
          </FactList>
        </TwoCol>
      </Inner>
    </Section>

  </>
);

export default About;
